import { decamelize } from 'humps'
import {
  blockSurveyInitialState,
  blockRichTextInitialState,
  blockTaskInitialState,
  blockMultimediaInitialState,
  blockEvaluationInitialState,
  blockScormInitialState,
} from 'models/block.model'
import { ICourseModel } from 'models/course.model'
import { OrderCoursesEnum } from 'enum/orderCourses.enum'
import { BlockTypes } from 'enum/block-type.enum'
import {
  ILikerOptionModel,
  ICreateQuestionLikertModel,
} from 'models/questionLikert.model'
import {
  blockMeetingInitialState,
  blockSessionInitialState,
} from '../models/block.model'

export const ColorHexToRgbWithAlpha = (color: string, alpha: number) => {
  const hex = color.replace('#', '')
  const r = parseInt(hex.substring(0, 2), 16)
  const g = parseInt(hex.substring(2, 4), 16)
  const b = parseInt(hex.substring(4, 6), 16)

  return {
    background: `rgba(${r},${g},${b},${alpha})`,
  }
}

function buildFormData(formData: FormData, data: any, parentKey: string) {
  if (
    data &&
    typeof data === 'object' &&
    !(data instanceof Date) &&
    !(data instanceof File)
  ) {
    Object.keys(data).forEach((key) => {
      buildFormData(
        formData,
        data[key],
        parentKey ? `${parentKey}[${key}]` : key
      )
    })
  } else {
    const value = data == null ? '' : data
    formData.append(parentKey, value)
  }
}
export const ObjectToFormDataWithFiles = (
  data: any,
  mainKey: string
): FormData => {
  const bodyFormData = new FormData()
  buildFormData(bodyFormData, data, mainKey)
  return bodyFormData
}

/**
 * Scale question payload of the per-question endpoints. Options removed from
 * the editor are no longer in `answerData`; when they were saved, their ids
 * arrive in `removedLabelIds` so the API deletes them.
 */
export const prepareLikertQuestionData = (
  questionData: any,
  answerData: ILikerOptionModel[],
  index: number,
  blockId: any,
  removedLabelIds: number[] = []
): ICreateQuestionLikertModel<BlockTypes.BlockSurvey> => {
  const removedOptions: ILikerOptionModel[] = removedLabelIds.map((id) => ({
    id,
    _destroy: true,
  }))
  const data: ICreateQuestionLikertModel<BlockTypes.BlockSurvey> = {
    likert: {
      likertLabelsAttributes: answerData
        .reduce<ILikerOptionModel[]>((prev, { id, label }, index: number) => {
          if (label || id) {
            let preparedData: ILikerOptionModel | null = null
            if (id) {
              if (label) preparedData = { id, label, order: index + 1 }
              else preparedData = { id, _destroy: true }
            } else if (label) preparedData = { label, order: index + 1 }
            if (preparedData) prev.push(preparedData)
          }
          return prev
        }, [])
        .concat(removedOptions),
    },
    question: {
      name: questionData.name,
      order: index,
      blockInstanceableType: BlockTypes.BlockSurvey,
      blockInstanceableId: blockId,
      mandatory: questionData.mandatory ?? false,
    },
  }
  return data
}
// TODO UNIFICAR ESTE MÉTODO CON EL DE ARRIBA
export const prepareOpenEndedData = (
  questionData: any,
  index: number,
  blockId: any
) => {
  const data = {
    open_ended: {
      id: questionData.questionableId || null,
    },
    question: {
      name: questionData.name,
      order: index,
      block_instanceable_type: BlockTypes.BlockSurvey,
      block_instanceable_id: blockId,
      mandatory: questionData.mandatory || false,
    },
  }
  return data
}

export const prepareBlockSurveyBlockData = (lessonId: any, order: number) => {
  return {
    ...blockSurveyInitialState,
    block_instance: {
      ...blockSurveyInitialState['block_instance'],
      lesson_id: Number(lessonId),
      order,
    },
  }
}
// Pueden unificarse, pero en un futuro, ambos tipos pueden cambiar
export const prepareBlockRichTextData = (lessonId: any, order: number) => {
  return {
    ...blockRichTextInitialState,
    block_instance: {
      ...blockRichTextInitialState['block_instance'],
      lesson_id: Number(lessonId),
      order,
    },
  }
}

export const prepareBlockTaskData = (lessonId: any, order: number) => {
  return {
    ...blockTaskInitialState,
    block_instance: {
      ...blockTaskInitialState['block_instance'],
      lesson_id: Number(lessonId),
      order,
    },
  }
}

export const prepareBlockEvaluationData = (lessonId: any, order: number) => {
  return {
    ...blockEvaluationInitialState,
    block_instance: {
      ...blockEvaluationInitialState['block_instance'],
      lesson_id: Number(lessonId),
      order,
    },
  }
}

export const prepareBlockMultimediaData = (lessonId: any, order: number) => {
  return {
    ...blockMultimediaInitialState,
    block_instance: {
      ...blockMultimediaInitialState['block_instance'],
      lesson_id: Number(lessonId),
      order,
    },
  }
}
export const prepareBlockScormData = (lessonId: any, order: number) => {
  return {
    ...blockScormInitialState,
    block_instance: {
      ...blockScormInitialState['block_instance'],
      lesson_id: Number(lessonId),
      order,
    },
  }
}
export const prepareBlockMeetingData = (
  lessonId: number | string,
  order: number
) => {
  return {
    ...blockMeetingInitialState,
    block_instance: {
      ...blockMeetingInitialState['block_instance'],
      lesson_id: Number(lessonId),
      order,
    },
  }
}

export const prepareBlockSessionData = (
  lessonId: number | string,
  order: number
) => {
  return {
    ...blockSessionInitialState,
    block_instance: {
      ...blockSessionInitialState['block_instance'],
      lesson_id: Number(lessonId),
      order,
    },
  }
}

export const getS3FileId = (url: string) => {
  const result = (url || '').match(/\/cache\/([^?]+)/)
  return (result && result[1]) || ''
}

export const decamelizeThatDontBreaksFile = (object: any): any => {
  if (object && !(object instanceof File)) {
    if (object instanceof Array) {
      return object.map((item) => decamelizeThatDontBreaksFile(item))
    }
    if (typeof object === 'object') {
      return Object.keys(object).reduce(
        (acc, next) => ({
          ...acc,
          [decamelize(next)]: decamelizeThatDontBreaksFile(object[next]),
        }),
        {}
      )
    }
  }
  return object
}

export const orderCoursesList = (
  courses: ICourseModel[],
  type: OrderCoursesEnum
): ICourseModel[] => {
  switch (type) {
    case OrderCoursesEnum.InProgress:
      return courses.sort(
        (
          { mandatory: aMandatory, advance: aAdvance, enrol: aEnrol },
          { mandatory: bMandatory, advance: bAdvance, enrol: bEnrol }
        ) => {
          if (aMandatory && !bMandatory) return -1
          if (!aMandatory && bMandatory) return 1
          return bAdvance - aAdvance !== 0
            ? bAdvance - aAdvance
            : new Date((aEnrol && aEnrol.createdAt) || '') <
              new Date((bEnrol && bEnrol.createdAt) || '')
            ? 1
            : -1
        }
      )
    case OrderCoursesEnum.finished:
      return courses.sort(
        ({ advance: aAdvance }, { advance: bAdvance }) => bAdvance - aAdvance
      )
    case OrderCoursesEnum.avaliable:
      return courses.sort(
        (
          { mandatory: aMandatory, createdAt: aCreatedAt },
          { mandatory: bMandatory, createdAt: bCreatedAt }
        ) => {
          if (aMandatory && !bMandatory) return -1
          if (!aMandatory && bMandatory) return 1
          return new Date(aCreatedAt) < new Date(bCreatedAt) ? 1 : -1
        }
      )
    default:
      break
  }
  return courses
}

export const truncStringMiddle = (name: string, onDesktop: boolean) => {
  try {
    const maxLength = onDesktop ? 48 : 40

    if (name.length <= maxLength) return name

    if (name.indexOf(' ') !== -1) {
      let result = name.substring(0, maxLength)
      result = result.substring(0, result.lastIndexOf(' ')) + ' (...)'
      return result
    }
    return name.substring(0, maxLength) + ' (...)'
  } catch {
    return name
  }
}

export const truncateNameKeepExtension = (filename: string) => {
  const extensionDot = filename.lastIndexOf('.')
  if (extensionDot < 17) return filename

  const ext = filename.slice(extensionDot)
  const name = filename.slice(0, extensionDot)
  return name.slice(0, 17) + '..' + ext
}

export const firstLetterUppercase = (text: string) => {
  return text.charAt(0).toUpperCase() + text.slice(1)
}
